import {
  SESSION_ERROR_CODES,
  SESSION_PATHS,
  type SessionErrorCode,
  type SessionExchangeRequest,
  type SessionExchangeResponse,
  sessionUrl,
} from '@words-happen/app-urls';
import { SessionError, type SessionFailure } from './session-failure';

const FAILURES_BY_CODE: Readonly<Record<SessionErrorCode, SessionFailure>> = {
  [SESSION_ERROR_CODES.emailNotVerified]: 'email-not-verified',
  [SESSION_ERROR_CODES.tokenInvalid]: 'token-invalid',
  [SESSION_ERROR_CODES.tokenStale]: 'stale',
  [SESSION_ERROR_CODES.originNotAllowed]: 'origin-not-allowed',
};

/** Exchanges a fresh Firebase ID token for the session cookies; resolves the URL the API validated, or null. */
export type ExchangeSession = (idToken: string, returnTo: string | null) => Promise<SessionExchangeResponse>;

/** Clears the session cookies. */
export type EndSession = () => Promise<void>;

export interface SessionClient {
  exchange: ExchangeSession;
  endSession: EndSession;
}

const errorCodeOf = async (response: Response): Promise<unknown> => {
  try {
    return ((await response.json()) as { errorCode?: unknown }).errorCode;
  } catch {
    return undefined;
  }
};

/** The failure for a response that isn't a success. */
export const responseFailure = async (response: Response): Promise<SessionFailure> => {
  if (response.status >= 500) {
    return 'server';
  }
  if (response.status === 403) {
    return FAILURES_BY_CODE[(await errorCodeOf(response)) as SessionErrorCode] ?? 'unexpected';
  }
  return 'unexpected';
};

/** words-happen-gql's session endpoints on `apiOrigin`, sending and receiving the cookies (credentials: include). */
export const createSessionClient = (apiOrigin: string, fetchImpl: typeof fetch = fetch): SessionClient => {
  const post = async (path: (typeof SESSION_PATHS)[keyof typeof SESSION_PATHS], body?: object) => {
    let response: Response;
    try {
      response = await fetchImpl(sessionUrl(apiOrigin, path), {
        method: 'POST',
        credentials: 'include',
        headers: body ? { 'content-type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch {
      throw new SessionError('network');
    }
    if (!response.ok) {
      throw new SessionError(await responseFailure(response));
    }
    return response;
  };

  return {
    exchange: async (idToken, returnTo) => {
      const response = await post(SESSION_PATHS.exchange, { idToken, returnTo } satisfies SessionExchangeRequest);
      const { returnUrl } = (await response.json()) as Partial<SessionExchangeResponse>;
      return { returnUrl: typeof returnUrl === 'string' ? returnUrl : null };
    },
    endSession: async () => {
      await post(SESSION_PATHS.signOut);
    },
  };
};
